import type { FormikHelpers } from "formik";
import { Formik } from "formik";
import { useEffect, useMemo, useState } from "react";
import {
	initialValues,
	validationSchema,
	getSchemas,
	t2InitialValues,
} from "./livestockManagement";
import {
	useAppSelector,
	useThreadsProcessor,
} from "@app/hooks";
import GenericModule from "../GenericModule";
import GenericTierTwo from "../GenericTierTwo";
import { BEModulesClassNames, FEModules } from "@utils/moduleList";
import { selectCurrentProject } from "@features/project/projectSlice";
import {
	useContextAwareGetLivestockDefaults,
	useContextAwareGetLivestock,
	useUpdateLivestockMutation,
} from "@hooks/index";
import useModuleHook from "../useModuleHook";
import type { ModuleFull } from "./livestockManagementTypes";
import useErrorMessage from "@utils/useErrorMessage";
import { selectCurrentBuilder } from "@features/builder/builderSlice";

const Livestock = () => {
	const { activeActivityId } = useAppSelector(selectCurrentProject);
	const { moduleList } = useAppSelector(selectCurrentBuilder);

	const id = moduleList.find(({ id }) => id === FEModules.LivestockManagement)?.uniqueId;

	const {
		data: livestocksData,
		isLoading,
		error,
		isError,
	} = useContextAwareGetLivestock(id ?? 0, {
		refetchOnMountOrArgChange: true,
	});
	const [
		updateLivestock,
		{ error: updateError, isError: isUpdateError, isLoading: isLoadingUpdate },
	] = useUpdateLivestockMutation();

	const { data: defaults, error: defaultsError, isError: isDefaultsError, isLoading: isLoadingDefaults } = useContextAwareGetLivestockDefaults(
		livestocksData?.module.id ?? 0,
		{ skip: !livestocksData?.module.id },
	);
	const { errorMsg: defaultsErrorMsg } = useErrorMessage({ isError: isDefaultsError, error: defaultsError });


	const { moduleSchema, t2Schema } = useMemo(() => getSchemas(), []);
	const [initValues, setInitValues] = useState<ModuleFull>({
		module: initialValues,
		tiertwo: t2InitialValues,
	});
	const { errorMsg } = useModuleHook({
		skeletonsLoadingDeps: [isLoading, isLoadingDefaults],
		isMutateError: isUpdateError,
		mutateError: updateError,
	});
	const { errorMsg: blockingError } = useErrorMessage({ isError, error });
	const { processModuleThreads } = useThreadsProcessor<ModuleFull>();

	useEffect(() => {
		if (livestocksData)
			setInitValues({
				module: { ...livestocksData.module, ...defaults },
				tiertwo: {
					...livestocksData.tiertwo, ...defaults,
				},
			});
		processModuleThreads({
			module: livestocksData?.module,
		})
	}, [livestocksData, defaults, processModuleThreads]);

	const handleSubmit = async (
		values: ModuleFull,
		{ resetForm }: FormikHelpers<ModuleFull>,
	) => {
		const module = { ...values.module };
		const payload = {
			data: { ...values, module },
			activityId: activeActivityId ?? 0,
		};

		try {
			values.module.id && (await updateLivestock(payload).unwrap());
			resetForm({ values });
		} catch (error) {
			console.error(error);
		}
	};

	return (
		<Formik
			validationSchema={validationSchema}
			initialValues={initValues}
			onSubmit={handleSubmit}
			enableReinitialize
		>
			<>
				<GenericModule
					title="project.livestock_management"
					t2Schema={t2Schema}
					moduleSchema={moduleSchema}
					beModuleType={BEModulesClassNames.LivestockManagement}
					isLoading={isLoadingUpdate}
					footerError={errorMsg}
					blockingError={blockingError}
					closingNotes="* Please note that for some livestock categories, including buffalo, deer and ostrich, there are no Tier1 parametrs for each of the livestock productivity levels. As a result changing livestock productivity will not trigger change in the emission levels. You may use Tier 2 section to refine the parameters."
					isError={isUpdateError}
				/>
				<GenericTierTwo
					title="project.livestock_management"
					t2Schema={t2Schema}
					warningMessage={defaultsErrorMsg ?? null}
				/>
			</>
		</Formik>
	);
};

export default Livestock;
